import useEvmChain from '@plug-core/hooks/useEVMChain'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { Avatar, cn } from '@ui'
import React, { FC } from 'react'

interface ChainLogoProps {
  chain?: string
  size?: number
  logo?: string
  className?: string
}

const ChainLogo: FC<ChainLogoProps> = ({ chain, size, logo, className }) => {
  const evmChain = useEvmChain(chain)

  return (
    <img
      alt={`chain-${chain}-logo`}
      src={logo || evmChain?.icon || getChainIcon(chain!)}
      className={cn('block shrink-0 rounded-full object-cover', className)}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * A token avatar with its chain logo(s) as small overlapping badges on the bottom-right corner.
 * Up to `maxChains` logos are shown left to right (later ones on top), then a "+N" pill.
 */
export const TokenChainAvatar = ({
  logo,
  fallback,
  chains,
  size = 40,
  maxChains = 2,
  badgeClassName = 'bg-[var(--row-bg,var(--plug-surface))] transition-colors duration-150',
}: {
  logo?: string
  /** Initial shown when there is no logo. */
  fallback?: string
  chains: (string | undefined)[]
  size?: number
  maxChains?: number
  /** Ring behind each badge. Defaults to the enclosing ListRow background (--row-bg), so it tracks hover. */
  badgeClassName?: string
}) => {
  const unique = Array.from(new Set(chains.filter(Boolean))) as string[]
  const visible = unique.slice(0, maxChains)
  const extra = unique.length - visible.length
  const badge = Math.max(12, Math.round(size * 0.4))
  const ring = 2
  const outer = badge + ring * 2
  const overlap = Math.round(outer * 0.4)

  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <Avatar src={logo} size={size} fallback={fallback} />
      {unique.length > 0 && (
        <span
          className="absolute flex items-center"
          style={{ right: -ring - 1, bottom: -ring - 1 }}
        >
          {visible.map((chain, index) => (
            <span
              key={chain}
              className={cn(
                'relative grid shrink-0 place-items-center rounded-full',
                badgeClassName
              )}
              style={{
                width: outer,
                height: outer,
                marginLeft: index ? -overlap : 0,
                zIndex: index + 1,
              }}
            >
              <ChainLogo chain={chain} size={badge} />
            </span>
          ))}
          {extra > 0 && (
            <span
              className={cn(
                'relative grid shrink-0 place-items-center rounded-pill font-text leading-none font-semibold text-fg tabular',
                badgeClassName
              )}
              style={{
                height: outer,
                minWidth: outer,
                padding: `0 ${ring + 1}px`,
                marginLeft: -overlap,
                zIndex: visible.length + 1,
                fontSize: Math.max(9, Math.round(badge * 0.62)),
              }}
            >
              <span
                className="grid h-full min-w-full place-items-center rounded-pill bg-surface-2 px-1"
                style={{ height: badge }}
              >
                +{extra}
              </span>
            </span>
          )}
        </span>
      )}
    </span>
  )
}

export default ChainLogo
